/**
 * Chat widget presented inside the launcher's Popover (panel mode) or a fullscreen Modal.
 * Uses AI SDK useChat with a custom WebSocket transport for streaming.
 * Delegates per-message rendering to src/components/chat/ registry.
 *
 * The chrome (header, bubbles, empty state, input, banners) is built entirely from
 * Meridian primitives so it stays consistent with the rest of the app and follows the
 * app's light theme. Presentation and sizing are owned by the side panel, not here.
 */
import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import { useChat } from '@ai-sdk/react';
import { type UIMessage } from 'ai';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Menu, { MenuItem } from '@amzn/meridian/menu';
import Message from '@amzn/meridian/message';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Textarea from '@amzn/meridian/textarea';
import Tooltip from '@amzn/meridian/tooltip';
import chevronDownSmallToken from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import clockTokens from '@amzn/meridian-tokens/base/icon/clock';
import expandToken from '@amzn/meridian-tokens/base/icon/expand';
import collapseToken from '@amzn/meridian-tokens/base/icon/collapse';
import menuMeatballToken from '@amzn/meridian-tokens/base/icon/menu-meatball';
import plusTokens from '@amzn/meridian-tokens/base/icon/plus';
import sendSmallTokens from '@amzn/meridian-tokens/base/icon/send-small';
import stopTokens from '@amzn/meridian-tokens/base/icon/stop';
import { useTranslation } from 'src/arb-wrapper';

import { AgentIcon } from 'src/components/chat/AgentIcon';
import { ChatHistoryView, type ResumableSession } from 'src/components/chat/ChatHistoryView';
import { DotLoader } from 'src/components/chat/DotLoader';
import { ChatMessage } from 'src/components/chat/ChatMessage';
import { type ChatMode } from 'src/components/chat/types';
import { getBubbleMaxWidth } from 'src/components/chat/ui-tools';
import { getIdToken } from 'src/lib/chat/auth';
import { AgentCoreWebSocketTransport } from 'src/lib/chat/transport';
import { useUser } from 'src/contexts/UserContext';

interface ChatWidgetProps {
  open: boolean;
  onClose: () => void;
  mode?: ChatMode;
  onModeChange?: (mode: ChatMode) => void;
  context?: string | null;
  entity?: { entityId: string; entityType: string } | null;
  /** Pass an existing sessionId to resume a previous session. Omit for new sessions. */
  initialSessionId?: string | null;
  onNewSession?: (sessionId: string) => void;
  onResume?: (session: ResumableSession) => void;
  /** 'data' switches to data-assistant copy and keeps the page context across history loads. */
  assistantVariant?: 'campaign' | 'data';
}

// alignSelf rather than auto margins: Meridian Column forces !important margins on its direct children.
const FULLSCREEN_MESSAGES_STYLE: React.CSSProperties = { alignSelf: 'center', maxWidth: 900, width: '100%' };

const getNextMessageForCollectFields = (
  message: UIMessage,
  messageIndex: number,
  messages: readonly UIMessage[],
): UIMessage | undefined => {
  if (message.role !== 'assistant') return undefined;
  const collectFieldsParts = message.parts.filter(part => part.type === 'tool-collect_fields');
  return collectFieldsParts.length === 1 ? messages[messageIndex + 1] : undefined;
};

export const ChatWidget: React.FC<ChatWidgetProps> = ({ open, onClose, mode = 'panel', onModeChange, onNewSession, onResume, context, entity, initialSessionId, assistantVariant = 'campaign' }) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const transport = useMemo(() => new AgentCoreWebSocketTransport(), []);
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [abortMessage, setAbortMessage] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef();
  const isSupersededAbortPending = useRef(false);
  const chatOptions = {
    transport,
    ...(sessionId ? { id: sessionId } : {}),
    onFinish: ({ isAbort }: { isAbort: boolean }) => {
      // useChat routes every Chat's onFinish here, so the previous session's stop would otherwise land on the new one.
      if (isAbort && isSupersededAbortPending.current) {
        isSupersededAbortPending.current = false;
        return;
      }
      if (isAbort) setAbortMessage('The response was interrupted — the request took too long. Please try again.');
    },
  };
  const { messages, setMessages, status, error, sendMessage, stop, clearError } = useChat(chatOptions);
  const chatStateRef = useRef({ setMessages, status, stop });
  chatStateRef.current = { setMessages, status, stop };
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const isLoading = status === 'streaming' || status === 'submitted';

  // Set the userId on the transport so the presigned URL includes the user identity.
  useEffect(() => {
    if (user?.username) {
      transport.setUserId(user.username);
    }
  }, [user?.username, transport]);

  // Authenticate AgentCore with the Cognito identity established for the current user.
  useEffect(() => {
    if (user?.username) {
      getIdToken().then(token => {
        if (token) transport.setIdToken(token);
      });
    }
  }, [user?.username, transport]);

  // Set entity context for agent-sessions tracking when campaign context is available.
  useEffect(() => {
    if (entity) {
      transport.setEntity(entity.entityId, entity.entityType);
    } else {
      transport.setEntity(null);
    }
  }, [entity, transport]);

  // Populate messages from server-sent history frame on reconnect.
  const historyReceived = useRef(false);
  useEffect(() => {
    transport.onHistory = (historyMessages) => {
      if (historyMessages.length > 0) {
        historyReceived.current = true;
        // Saved history omits the bootstrap context frame; dashboards need it back so later sends keep the platform.
        const hasContext = historyMessages.some(msg => msg.parts?.some(p => p.type === 'text' && p.text.startsWith('[Context:')));
        setMessages(assistantVariant === 'data' && context && !hasContext
          ? [{ id: `context-${crypto.randomUUID()}`, role: 'user', parts: [{ type: 'text', text: `[Context: ${context}]` }] }, ...historyMessages]
          : historyMessages);
      }
    };
    return () => { transport.onHistory = null; };
  }, [transport, setMessages, assistantVariant, context]);

  // On open: use initialSessionId (returning session) or generate a new one (new session).
  const bootstrapSent = useRef(false);
  const bootstrapTargetSessionId = useRef<string | undefined>();
  const wasOpen = useRef(false);
  const previousInitialSessionId = useRef(initialSessionId);
  const onNewSessionRef = useRef(onNewSession);

  useEffect(() => {
    onNewSessionRef.current = onNewSession;
  }, [onNewSession]);

  const resetSession = useCallback((nextSessionId: string) => {
    const { setMessages: setCurrentMessages, status: currentStatus, stop: stopCurrentResponse } = chatStateRef.current;
    setHistoryOpen(false);
    if (currentStatus === 'streaming' || currentStatus === 'submitted') {
      isSupersededAbortPending.current = true;
      void stopCurrentResponse();
    }
    transport.disconnect();
    setCurrentMessages([]);
    setSessionId(nextSessionId);
    bootstrapSent.current = false;
    bootstrapTargetSessionId.current = nextSessionId;
    historyReceived.current = false;
    setAbortMessage(null);
  }, [transport]);

  const startNewSession = useCallback((notifyRouter = false) => {
    const newSessionId = crypto.randomUUID();
    resetSession(newSessionId);
    if (notifyRouter) onNewSessionRef.current?.(newSessionId);
  }, [resetSession]);

  useEffect(() => {
    const priorInitialSessionId = previousInitialSessionId.current;
    previousInitialSessionId.current = initialSessionId;

    if (!open) {
      wasOpen.current = false;
      transport.disconnect();
      return;
    }

    if (!wasOpen.current) {
      wasOpen.current = true;
      if (initialSessionId) {
        resetSession(initialSessionId);
      } else {
        startNewSession();
      }
    } else if (initialSessionId && initialSessionId !== priorInitialSessionId && initialSessionId !== sessionId) {
      resetSession(initialSessionId);
    }
  }, [open, transport, initialSessionId, sessionId, resetSession, startNewSession]);

  // Auto-send context on open to trigger WebSocket connection and history load.
  // The server decides whether to respond based on whether history exists.
  useEffect(() => {
    // A reset and a context change can land in one commit; wait until the new session is rendered.
    if (open && sessionId && sessionId === bootstrapTargetSessionId.current && context && user?.username && !bootstrapSent.current) {
      bootstrapSent.current = true;
      sendMessage({ text: `[Context: ${context}]` });
    }
    if (!open) {
      bootstrapSent.current = false;
    }
  }, [open, sessionId, context, user?.username, sendMessage]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (open && !isLoading && !historyOpen) inputRef.current?.querySelector('textarea')?.focus();
  }, [open, isLoading, historyOpen]);

  if (!open || !sessionId) return null;

  const fullscreenLabel = t(mode === 'panel' ? 'chat_widget_fullscreen' : 'chat_widget_exit_fullscreen');

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;
    setInput('');
    clearError();
    setAbortMessage(null);
    const text = context && messages.length === 0
      ? `[Context: ${context}]\n\n${trimmed}`
      : trimmed;
    sendMessage({ text });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleResume = (session: ResumableSession) => {
    setHistoryOpen(false);
    onResume?.(session);
  };

  return (
    <Column backgroundColor="principal" data-testid="chat-widget" height="100%" heights={historyOpen ? ['fit', 'fit', 'fill'] : ['fit', 'fit', 'fill', 'fit', 'fit']} overflowY="hidden" spacing="none">
      <Row alignmentHorizontal="justify" alignmentVertical="center" spacingInset="300 400" width="100%">
        <Row alignmentVertical="center" spacing="200">
          <AgentIcon size={24} />
          <Text type="h100">{t(assistantVariant === 'data' ? 'chat_widget_title_data_assistant' : 'chat_widget_title')}</Text>
        </Row>
        <Row alignmentVertical="center" spacing="100">
          <Tooltip position="bottom" title={t('chat_widget_close')}>
            <Button aria-label={t('chat_widget_close')} data-testid="chat-widget-close" size="small" type="icon" onClick={onClose}>
              <Icon tokens={chevronDownSmallToken} />
            </Button>
          </Tooltip>
          <Button
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            aria-label={t('chat_widget_more_options')}
            data-testid="chat-widget-menu"
            ref={menuButtonRef}
            size="small"
            type="icon"
            onClick={() => { setMenuOpen((isOpen) => !isOpen); }}
          >
            <Icon tokens={menuMeatballToken} />
          </Button>
          <Menu
            anchorNode={menuButtonRef.current}
            open={menuOpen}
            position="bottom"
            onClose={() => { setMenuOpen(false); }}
          >
            <MenuItem data-testid="chat-widget-new-session" iconTokens={plusTokens} onClick={() => { setMenuOpen(false); startNewSession(true); }}>
              {t('chat_widget_new_campaign')}
            </MenuItem>
            <MenuItem data-testid="chat-widget-history" iconTokens={clockTokens} onClick={() => { setMenuOpen(false); setHistoryOpen((isOpen) => !isOpen); }}>
              {t('chat_widget_history')}
            </MenuItem>
            <MenuItem data-testid="chat-widget-fullscreen" iconTokens={mode === 'panel' ? expandToken : collapseToken} onClick={() => { setMenuOpen(false); onModeChange?.(mode === 'panel' ? 'fullscreen' : 'panel'); }}>
              {fullscreenLabel}
            </MenuItem>
          </Menu>
        </Row>
      </Row>
      <Divider />

      {historyOpen
        ? (
          <Column height="100%" overflowY="auto" spacing="none">
            <ChatHistoryView
              currentEntityId={entity?.entityId}
              currentUser={user?.username ?? ''}
              onClose={() => { setHistoryOpen(false); }}
              onResume={handleResume}
            />
          </Column>
          )
        : (
          <>
            {/* Messages */}
            <Column data-testid="chat-widget-messages" overflowY="auto" spacingInset="400">
              <div data-testid="chat-widget-messages-content" style={mode === 'fullscreen' ? FULLSCREEN_MESSAGES_STYLE : undefined}>
                <Column spacing="450">
                  {messages.length === 0 && !isLoading && !error && (
                    <Column alignmentHorizontal="center" data-testid="chat-widget-intro" spacing="300" spacingInset="500 400">
                      <AgentIcon size={48} />
                      <Heading level={3}>{t('chat_agent_name')}</Heading>
                      <Text alignment="center" color="secondary" type="b300">{t(assistantVariant === 'data' ? 'chat_widget_empty_state_data_assistant' : 'chat_widget_empty_state')}</Text>
                    </Column>
                  )}
                  {messages.filter(msg => !(msg.role === 'user' && msg.parts?.every(p => p.type === 'text' && (p as { text: string }).text.startsWith('[Context:')))).map((msg) => {
                    const nextMessage = getNextMessageForCollectFields(msg, messages.indexOf(msg), messages);
                    return (
                      <ChatMessage
                        agentName={t('chat_agent_name')}
                        isLastMessage={msg === messages[messages.length - 1]}
                        isLoading={isLoading}
                        key={msg.id}
                        message={msg}
                        mode={mode}
                        nextMessage={nextMessage}
                        userName={t('chat_you_label')}
                        onSend={(text) => { sendMessage({ text }); }}
                      />
                    );
                  })}
                  {isLoading && messages[messages.length - 1]?.role === 'user' && (
                    <Message
                      data-chat-bubble=""
                      data-testid="chat-widget-loading"
                      maxWidth={getBubbleMaxWidth(mode)}
                      name={t('chat_agent_name')}
                      showAvatar={false}
                      showMetadata={false}
                      size="medium"
                      type="incoming"
                    >
                      <DotLoader accessibleLabel={t('chat_widget_thinking')} />
                    </Message>
                  )}
                  {error && (
                    <Alert data-testid="chat-widget-error" size="small" type="error">
                      {t('chat_widget_error_message')}
                    </Alert>
                  )}
                  {abortMessage && !error && (
                    <Alert size="small" type="warning">
                      {abortMessage}
                    </Alert>
                  )}
                  <div ref={messagesEndRef} />
                </Column>
              </div>
            </Column>

            <Divider />
          </>
          )}

      {/* Input */}
      {!historyOpen && (
        <Column backgroundColor="highlight" spacing="350" spacingInset="400">
          <div ref={inputRef} onKeyDown={handleKeyDown}>
            <Textarea
              data-testid="chat-widget-input"
              disabled={isLoading}
              placeholder={t('chat_widget_input_placeholder')}
              resize="none"
              rows={2}
              value={input}
              onChange={setInput}
            />
          </div>
          <Row alignmentHorizontal="end" alignmentVertical="center" width="100%">
            {isLoading
              ? (
                <Button circular data-testid="chat-widget-stop" type="secondary" onClick={() => stop()}>
                  {t('chat_widget_stop_button')} <Icon tokens={stopTokens} />
                </Button>
                )
              : (
                <Button circular data-testid="chat-widget-send" disabled={!input.trim()} type="secondary" onClick={handleSend}>
                  {t('chat_widget_send_button')} <Icon tokens={sendSmallTokens} />
                </Button>
                )}
          </Row>
          <Divider />
          <Alert size="small" type="neutral">
            {t('chat_widget_accuracy_notice')}
          </Alert>
        </Column>
      )}
    </Column>
  );
};
